// Which party set a job uses, and at which level each party is. ONE definition for the Sync Preflight, the sync itself and the address links.
//
// A job carries two sets of parties: plain (importer_id / consignor_id / notify_id = the MBL / master side) and HBL
// (hbl_importer_id / hbl_consignor_id / hbl_notify_id = the house side). BoxTop gets ONE set per job:
//   - a consol child                                   -> HBL set (falls back to plain for a party it has no HBL value for)
//   - a one-master-one-house job (master AND house no.) -> HBL set, same fallback  (the declaration's consignor/consignee are the HBL's)
//   - a consol master, or any other job                -> plain set
// The master B/L / AWB number itself is always sent (BLNumber / AWBMaster), whichever set carries the parties.
//
// HBL parties belong to the customer (their BoxTop address is searched under the customer's account). MBL-level parties are independent of
// the customer: searched under their OWN account first, then the customer's (search only, flagged), never created.
// Run: node --test --experimental-strip-types supabase/functions/_shared/partyScope.test.ts
export type PartyLevel = 'hbl' | 'mbl' | 'plain'

export interface ScopeJob {
  parent_job_id?: string | null
  job_type?: string | null
  hbl_importer_id?: string | null
  hbl_consignor_id?: string | null
  hbl_notify_id?: string | null
}

export interface DocFlags { hasMaster: boolean; hasHouse: boolean }

export type PartyField = 'consignor' | 'importer' | 'notify'

// Which document numbers does a job carry? ONE derivation for every caller (sync, declaration, invoice PDF, job screens, lists), so the
// party set can never be decided from differently read flags. Pass the job's sea and air details rows (either may be null/undefined).
export function docFlagsOf(
  sea?: { master_bl_no?: string | null; house_bl_no?: string | null } | null,
  air?: { mawb_no?: string | null; hawb_no?: string | null } | null,
): DocFlags {
  return { hasMaster: !!(sea?.master_bl_no || air?.mawb_no), hasHouse: !!(sea?.house_bl_no || air?.hawb_no) }
}

const hblIdOf = (job: ScopeJob, key: PartyField): string | null | undefined =>
  key === 'consignor' ? job.hbl_consignor_id : key === 'importer' ? job.hbl_importer_id : job.hbl_notify_id

const isConsolMaster = (job: ScopeJob): boolean => !job.parent_job_id && String(job.job_type ?? '').toUpperCase() === 'CONSOL'

// Does this job send its HBL set (where a party has an HBL value)?
export function usesHblSet(job: ScopeJob, flags: DocFlags): boolean {
  if (job.parent_job_id) return true
  return !isConsolMaster(job) && flags.hasMaster && flags.hasHouse
}

// The party this job sends for a field: id, the role name used for its address, and its level.
export function pickParty<T extends ScopeJob & Record<string, any>>(job: T, key: PartyField, flags: DocFlags): { id: string | null; role: string; level: PartyLevel } {
  const plainId = (job[`${key}_id`] as string | null | undefined) ?? null
  const hblId = hblIdOf(job, key)
  if (usesHblSet(job, flags) && hblId) return { id: hblId, role: `hbl_${key}`, level: 'hbl' }
  return { id: plainId, role: key, level: partyLevel(job, key, flags) }
}

export function partyLevel(job: ScopeJob, key: PartyField, flags: DocFlags): PartyLevel {
  if (usesHblSet(job, flags) && hblIdOf(job, key)) return 'hbl'
  if (job.parent_job_id) return 'mbl'
  if (isConsolMaster(job)) return 'mbl'
  return flags.hasMaster ? 'mbl' : 'plain'
}
